import React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import * as authService from "../api/auth";

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Alert,
  Collapse,
  Link,
} from "@mui/material";
import { PersonOutline } from "@mui/icons-material";

const Schema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required"),
});

export default function ForgotPassword() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: "linear-gradient(135deg, #87CEFA 0%, #4682B4 100%)",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          borderRadius: 4,
          p: 1,
          backgroundColor: "rgba(255,255,255,0.25)",
          boxShadow:
            "0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.25)",
          backdropFilter: "blur(12px)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Box
              component="img"
              src="/white_on_trans.png"
              alt="OnTheFarm logo"
              loading="lazy"
              sx={{
                display: "block",
                mx: "auto",
                mb: 1,
                width: { xs: 94, sm: 150, md: 190 },
                height: { xs: 94, sm: 150, md: 190 },
                objectFit: "contain",
              }}
            />
            <Typography variant="h6" color="#FFFFFF" sx={{ fontWeight: 600 }}>
              Forgot your password?
            </Typography>
            <Typography
              variant="body2"
              color="rgba(255,255,255,0.9)"
              sx={{ mt: 1 }}
            >
              Enter your email and we will send you a link to reset your
              password.
            </Typography>
          </Box>

          <Formik
            initialValues={{ email: "" }}
            validationSchema={Schema}
            validateOnMount
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setStatus(undefined);
              try {
                await authService.requestPasswordReset({
                  email: values.email.trim(),
                });
                navigate("/password-reset-sent", { replace: true });
              } catch (err) {
                const detail = err?.response?.data?.detail;
                setStatus({
                  serverError:
                    (Array.isArray(detail)
                      ? detail.map((x) => x.msg || x).join(" ")
                      : detail) ||
                    err?.message ||
                    "Something went wrong. Please try again.",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              isSubmitting,
              errors,
              touched,
              isValid,
              dirty,
              setFieldValue,
              status,
            }) => (
              <Form noValidate>
                <Collapse in={Boolean(status?.serverError)} unmountOnExit>
                  <Alert
                    severity="error"
                    variant="filled"
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    {status?.serverError}
                  </Alert>
                </Collapse>

                <Field
                  as={TextField}
                  name="email"
                  label="Email"
                  fullWidth
                  margin="normal"
                  autoComplete="email"
                  onBlur={(e) => setFieldValue("email", e.target.value.trim())}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.6)",
                    },
                  }}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting || !isValid || !dirty}
                  sx={{
                    mt: 2,
                    py: 1.3,
                    borderRadius: 2.5,
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                    fontWeight: 600,
                    boxShadow: "0 8px 18px rgba(0,0,0,0.2)",
                    background:
                      "linear-gradient(135deg, #1E90FF 0%, #00BFFF 100%)",
                    "&:hover": {
                      boxShadow: "0 10px 22px rgba(0,0,0,0.25)",
                      filter: "brightness(0.95)",
                    },
                  }}
                >
                  {isSubmitting ? "Sending…" : "Send reset link"}
                </Button>

                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Link component={RouterLink} to="/login" underline="hover">
                    Back to sign in
                  </Link>
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
}
